import React, { useCallback, useEffect, useState } from "react";
import Stepper from "../components/common/stepper";
import Close from "../assest/svg/close.svg";
import ProjectNameAndDate from "./ProjectNameAndDate";
import { useFormik } from "formik";
import { z } from "zod";
import { ProjectDataSchema } from "./projectDataSchema";
import PhaseAndTask from "./phaseAndTask";
import { toFormikValidationSchema } from "zod-formik-adapter";
import NonWorkingDays from "./nonWorkingDays";
import { useProjectCreate } from "../api/mutation/useCreateProjectExleMutation";
import FileGeneratingLoader from "./loader";
import { toast } from "react-toastify";

// Function to reorder tasks so milestone task comes last in each phase and update dependencies
const reorderTasksWithMilestoneLast = (phases: any[]) => {
  return phases.map((phase) => {
    const tasks = [...phase.tasks];
    
    // Find the first milestone task
    const firstMilestoneIndex = tasks.findIndex(task => task.milestone === true);
    
    if (firstMilestoneIndex === -1) {
      // No milestone tasks found, return as is
      return phase;
    }
    
    // Get the first milestone task
    const firstMilestoneTask = { ...tasks[firstMilestoneIndex] };
    
    // Filter out all milestone tasks except the first one
    const nonMilestoneTasks = tasks.filter(task => task.milestone !== true);
    
    // If there are non-milestone tasks, update milestone dependencies
    if (nonMilestoneTasks.length > 0) {
      // Make milestone depend on the last non-milestone task
      const lastNonMilestoneTask = nonMilestoneTasks[nonMilestoneTasks.length - 1];
      firstMilestoneTask.predecessorsList = [lastNonMilestoneTask.taskName];
    }
    
    // Add the first milestone task at the end
    const reorderedTasks = [...nonMilestoneTasks, firstMilestoneTask];
    
    return {
      ...phase,
      tasks: reorderedTasks
    };
  }).map((phase, phaseIndex, allPhases) => {
    // Second pass: Update inter-phase dependencies
    if (phaseIndex > 0) {
      const previousPhase = allPhases[phaseIndex - 1];
      const previousPhaseLastTask = previousPhase.tasks[previousPhase.tasks.length - 1];
      
      // Make the first task of current phase depend on the last task of previous phase (which is milestone if exists)
      if (phase.tasks.length > 0) {
        const firstTaskOfCurrentPhase = { ...phase.tasks[0] };
        
        // Update dependencies to include the last task of previous phase
        const existingDeps = firstTaskOfCurrentPhase.predecessorsList || [];
        const newDeps = [...existingDeps];
        
        // Remove any existing dependencies on tasks from previous phase to avoid duplicates
        const previousPhaseTaskNames = previousPhase.tasks.map((t: any) => t.taskName);
        const filteredDeps = newDeps.filter(dep => !previousPhaseTaskNames.includes(dep));
        
        // Add dependency on the last task of previous phase
        firstTaskOfCurrentPhase.predecessorsList = [...filteredDeps, previousPhaseLastTask.taskName];
        
        // Update the first task in the phase
        phase.tasks[0] = firstTaskOfCurrentPhase;
      }
    }
    
    return phase;
  });
};

const initialValues: z.infer<typeof ProjectDataSchema> = {
  project_name: "",
  projectStartDate: new Date(),
  phases: [{ phaseName: "Nouvelle phase 1", tasks: [{ taskName: "tâche_1_phase_1", duration: 1,milestone:false }] }],
  dependencies: [],
  nonWorkingDays: []
};
type Props = {
  close: () => void
}
function ProjectCreateForm(props: Props) {
  const [currentStep, setCurrentStep] = useState(1);

  const steppers = [
    { element: <>1</>, skip: false, heading: "Étape 1", subheading: "Cadrage WBS" },
    { element: <>2</>, skip: false, heading: "Étape 2", subheading: "Estimation Délais" },
    { element: <>3</>, skip: false, heading: "Étape 3", subheading: "Dépendances & Liens" },
    { element: <>4</>, skip: false, heading: "Étape 4", subheading: "Générer le planning" },
  ];
  const useProjectCreateMutation = useProjectCreate();
  const formik = useFormik<z.infer<typeof ProjectDataSchema>>({
    initialValues: initialValues,
    validationSchema: toFormikValidationSchema(ProjectDataSchema),
    onSubmit(values) {
      console.log(values);
      delete values.dependencies;
      const reorderedPhases = reorderTasksWithMilestoneLast(values.phases);
      const finalValues = { ...values, phases: reorderedPhases };
      delete finalValues.file;
      useProjectCreateMutation.mutate(finalValues, {
        async onSuccess(data) {
          await downloadFile(data.data, finalValues.project_name);
        },
        onError(error) {
          console.error(error);
          toast.error(
            error.message ?? "Oops! Something went wrong. Please try again ",
            { autoClose: false }
          );
        },
      });
    },
  });

  useEffect(() => {
    formik.validateForm();
  }, []);

  const downloadFile = async (fileUrl: string, projectName: string) => {
    try {
      // Extract the original file extension from the URL
      const fileExtension = fileUrl.split(".").pop() || "xlsx";

      // Create the filename with original extension
      const fileName =
        new Date().toISOString().split("T")[0] +
        "_" +
        projectName +
        "." +
        fileExtension;

      // Fetch the file with proper headers
      const response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          Accept:
            "application/octet-stream, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, */*",
        },
      });

      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);

      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);

      toast.success("File downloaded successfully!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download the file. Please try again.");
    }
  };

  const enableNext = useCallback(() => {
    switch (currentStep) {
      case 1:
        return Boolean(
          !formik.errors.project_name && !formik.errors.projectStartDate
        );
      case 2:
        return !Object.hasOwn(formik.errors, "phases");
      case 3:
        return true;
      case 4:
        return !Object.hasOwn(formik.errors, "nonWorkingDays");
      default:
        return false;
    }
  }, [formik.errors, currentStep]);

  return (
    <div className="w-full h-full flex flex-col justify-between overflow-hidden">
      <FileGeneratingLoader isGenerating={useProjectCreateMutation.isPending} />

      {/* Modal Header matching mockup */}
      <div className="flex items-center justify-between pb-3.5 mb-3 border-b border-slate-200 dark:border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-600/20 text-purple-400 border border-purple-500/30 flex items-center justify-center shadow-inner">
            <svg
              className="w-5 h-5 text-cyan-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
              />
            </svg>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-600 dark:text-cyan-400 font-semibold">
              CONTINUUM AI
            </div>
            <h2 className="text-base font-display font-bold text-slate-900 dark:text-white">
              Planning Gantt
            </h2>
          </div>
        </div>

        <button
          type="button"
          onClick={props.close}
          className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
          title="Fermer"
        >
          <img
            src={Close}
            className="w-4 h-4 dark:invert opacity-70 hover:opacity-100"
            alt="Fermer"
          />
        </button>
      </div>

      {/* Stepper Progress Bar */}
      <div className="shrink-0 px-2">
        <Stepper
          currentStep={currentStep}
          steppers={steppers}
          submitCount={formik.submitCount}
        />
      </div>

      {/* Active Step Content */}
      <div
        key={currentStep}
        className="flex-1 overflow-y-auto px-1 py-2 animate-fadeIn"
      >
        {currentStep === 1 && <ProjectNameAndDate formik={formik} />}
        {currentStep === 2 && <PhaseAndTask formik={formik} />}
        {currentStep === 3 && (
          <PhaseAndTask formik={formik} showDependency={true} />
        )}
        {currentStep === 4 && <NonWorkingDays formik={formik} />}
      </div>

      {/* Footer Navigation Buttons */}
      <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-200 dark:border-white/10 shrink-0">
        <div>
          {currentStep > 1 && (
            <button
              type="button"
              className="px-3.5 py-1.5 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-300/60 dark:border-white/10 transition-colors"
              onClick={() =>
                setCurrentStep(currentStep > 1 ? currentStep - 1 : 1)
              }
            >
              Retour
            </button>
          )}
        </div>

        <div className="flex items-center gap-2.5">
          {steppers[currentStep - 1]?.skip && (
            <button
              type="button"
              className="px-3 py-1.5 rounded-xl text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
              onClick={() =>
                setCurrentStep(
                  currentStep < steppers.length
                    ? currentStep + 1
                    : steppers.length
                )
              }
            >
              Passer
            </button>
          )}
          <button
            type="button"
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200 shadow-md ${
              !enableNext()
                ? "opacity-40 cursor-not-allowed bg-slate-200 dark:bg-white/10 text-slate-400"
                : "bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-500 hover:opacity-95 text-white shadow-glow-purple"
            }`}
            disabled={!enableNext()}
            onClick={() => {
              if (currentStep === steppers.length) {
                formik.submitForm();
              } else {
                setCurrentStep(
                  currentStep < steppers.length
                    ? currentStep + 1
                    : steppers.length
                );
              }
            }}
          >
            {currentStep < steppers.length ? "Suivant" : "Générer le planning"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProjectCreateForm;
